import { useEffect, useState, type FormEvent } from 'react';
import { LIBELLE_CATEGORIE_OPCVM, type CategorieOpcvm } from '@gsm/partage';
import { api, ErreurApi } from '../api/client';
import { messageErreur, useRessource } from '../api/ressource';
import { BandeauAlerte } from './BandeauAlerte';
import { Modale } from './Modale';

/** Ce que la fenêtre doit savoir d'un ordre OPCVM pour proposer ses fonds. */
export interface OrdreFonds {
  id: string;
  reference: string;
  instrumentId: string | null;
  instrument: { opcvm: { categorie: CategorieOpcvm } | null } | null;
  mandat: { profil: { charia: boolean } };
}

interface Fonds {
  id: string;
  libelle: string;
  chariaCompliant: boolean;
  opcvm: { categorie: CategorieOpcvm; societeGestion: string; fondsParDefaut: boolean } | null;
}

/**
 * Choix — ou confirmation — du fonds d'un ordre OPCVM (RG-16). Commune à
 * l'écran Ordres et à la fiche d'une campagne : la validation d'une campagne
 * exige que ses fonds soient confirmés, il faut pouvoir le faire sur place.
 *
 * La liste des fonds n'est chargée qu'à l'ouverture.
 */
export function ModaleFonds({
  ordre,
  onFermer,
  onConfirme,
}: {
  ordre: OrdreFonds | null;
  onFermer: () => void;
  onConfirme: () => Promise<unknown>;
}) {
  const fonds = useRessource<Fonds[]>(ordre ? '/instruments?type=OPCVM&statut=ACTIF' : null);
  const [fondsRetenu, setFondsRetenu] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  useEffect(() => {
    setFondsRetenu(ordre?.instrumentId ?? '');
    setErreur(null);
  }, [ordre]);

  /**
   * Fonds recevables : même catégorie — la cible s'exprime par catégorie, pas
   * par fonds — et conformes à la charia si le profil du mandat l'exige.
   * Proposer les autres reviendrait à faire cliquer sur un refus.
   */
  const eligibles = (fonds.donnees ?? []).filter(
    (f) =>
      f.opcvm !== null &&
      f.opcvm.categorie === ordre?.instrument?.opcvm?.categorie &&
      (!ordre.mandat.profil.charia || f.chariaCompliant),
  );

  async function confirmer(evenement: FormEvent) {
    evenement.preventDefault();
    if (!ordre) return;
    setErreur(null);
    setOccupe(true);
    try {
      await api.post(`/ordres/${ordre.id}/confirmer-fonds`, { instrumentId: fondsRetenu });
      await onConfirme();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? messageErreur(e) : 'Confirmation impossible.');
    } finally {
      setOccupe(false);
    }
  }

  return (
    <Modale
      ouverte={ordre !== null}
      onFermer={onFermer}
      surtitre={ordre?.reference}
      titre="Choisir le fonds"
      taille="large"
    >
      {erreur && (
        <BandeauAlerte nature="refus" titre="Confirmation refusée">
          {erreur}
        </BandeauAlerte>
      )}
      <p className="consigne consigne--tete">
        Le moteur a proposé le fonds par défaut de la catégorie{' '}
        {ordre?.instrument?.opcvm ? LIBELLE_CATEGORIE_OPCVM[ordre.instrument.opcvm.categorie] : '—'}
        . Retenir un autre fonds vaut confirmation : l’ordre pourra ensuite être soumis. Seuls les
        fonds actifs de cette catégorie sont proposés
        {ordre?.mandat.profil.charia
          ? ', et le profil du mandat étant charia, uniquement les fonds conformes'
          : ''}
        .
      </p>
      <form onSubmit={(e) => void confirmer(e)} noValidate>
        <div className="champ">
          <label className="surtitre" htmlFor="fonds">
            Fonds retenu
          </label>
          <select
            id="fonds"
            className="champ__select"
            value={fondsRetenu}
            onChange={(e) => setFondsRetenu(e.target.value)}
          >
            <option value="">—</option>
            {eligibles.map((f) => (
              <option key={f.id} value={f.id}>
                {f.libelle} — {f.opcvm?.societeGestion}
                {f.opcvm?.fondsParDefaut ? ' (proposé par défaut)' : ''}
              </option>
            ))}
          </select>
          {fonds.chargement ? (
            <span className="champ__aide">Chargement des fonds…</span>
          ) : eligibles.length === 0 ? (
            <span className="champ__message">
              Aucun fonds recevable au référentiel. Créez-en un, ou rendez un fonds existant actif
              et conforme.
            </span>
          ) : null}
        </div>
        <div className="modale__actions">
          <button type="button" className="bouton bouton--fantome" onClick={onFermer}>
            Annuler
          </button>
          <button className="bouton" type="submit" disabled={occupe || !fondsRetenu}>
            {occupe ? 'Confirmation…' : 'Confirmer ce fonds'}
          </button>
        </div>
      </form>
    </Modale>
  );
}
